import type { SegmentationSettings, SegmentationSettingsOptions } from '@/src/components/segmentation/segmentation-typing';

import { defineStore } from 'pinia';
import { ref } from 'vue';

import { isValidSettingKey, SEGMENTATION_SETTINGS_SCHEMA } from '@/src/components/segmentation/segmentation-typing';

const defaultSegmentationSettings: SegmentationSettings = {
  SEND_IMAGES_DIRECTLY: false,
  IMAGES_COMPRESSED: false,
  ENCRYPT_IMAGES: false,
  BATCH_SIZE: 8,
  RETURN_AS: 'polyline',
  POST_PROCESSING: 'smooth',
  SAX_MODEL_NAME: 'corseg',
  FUNCTION_MODULE_BEHAVIOR: 'auto',
};

export const useSegmentationSettingsStore = defineStore(
  'segmentationSettings',
  () => {
    const settings = ref<SegmentationSettings>({
      ...defaultSegmentationSettings,
    });

    function updateSettings(newSettings: Partial<SegmentationSettings>) {
      settings.value = { ...settings.value, ...newSettings };
    }

    function updateSettingsFromLabels(newSettings: Record<string, string>) {
      function updateSetting<K extends SegmentationSettingsOptions>(updatedSettings: Partial<SegmentationSettings>, key: K, value: string) {
        const setting = SEGMENTATION_SETTINGS_SCHEMA[key];
        if (!setting) return;
        const index = setting.optionLabels.indexOf(value);
        if (index === -1) return;
        updatedSettings[key] = setting.optionsRaw[index];
      }

      const updatedSettings: Partial<SegmentationSettings> = {};
      Object.entries(newSettings).forEach(([key, value]) => {
        if (!isValidSettingKey(key)) return;
        updateSetting(updatedSettings, key, value);
      });

      updateSettings(updatedSettings);
    }

    function resetSettings() {
      settings.value = { ...defaultSegmentationSettings };
    }

    function getSettings(): SegmentationSettings {
      return { ...settings.value };
    }

    return {
      settings,
      updateSettings,
      resetSettings,
      getSettings,
      updateSettingsFromLabels,
    };
  },
);
